/**
 * Coordinates token rotation across browser tabs.
 *
 * The server rotates (invalidates) the refresh token on every
 * refresh-session / refresh-access-token call. Without cross-tab
 * coordination, two tabs racing a rotation invalidate each other's
 * token and one tab gets a 401 that logs the user out everywhere
 * (tokens live in shared localStorage).
 *
 * Primary mechanism: Web Locks API (navigator.locks), which is
 * same-origin and works across tabs/windows.
 * Fallback: a module-scoped promise, which still de-duplicates
 * within the current tab when Web Locks are unavailable
 * (e.g. Firefox < 125 or non-secure contexts).
 */
export class TokenRefreshLock {

    private static readonly LOCK_NAME = 'auth_token_refresh';

    // Fallback mutex shared across all callers within this tab
    private static fallbackQueue: Promise<unknown> = Promise.resolve();

    private static supportsWebLocks(): boolean {
        return typeof navigator !== 'undefined'
            && !!navigator.locks
            && typeof navigator.locks.request === 'function';
    }

    /**
     * Runs the given callback exclusively, across all tabs of this origin.
     * The callback should READ THE CURRENT TOKENS FROM LOCALSTORAGE
     * (not capture them before calling this), so a caller that waited
     * for the lock automatically picks up tokens rotated by another tab.
     */
    static async withLock<T>(callback: () => Promise<T>): Promise<T> {
        if (this.supportsWebLocks()) {
            return navigator.locks.request(this.LOCK_NAME, callback);
        }

        return this.withFallbackMutex(callback);
    }

    private static async withFallbackMutex<T>(callback: () => Promise<T>): Promise<T> {
        const previous = this.fallbackQueue;
        let release: () => void;
        this.fallbackQueue = new Promise<void>(resolve => { release = resolve; });

        await previous.catch(() => undefined);
        try {
            return await callback();
        } finally {
            release!();
        }
    }

    /**
     * Test seam: forget queued fallback work.
     */
    static resetFallbackQueueForTesting(): void {
        this.fallbackQueue = Promise.resolve();
    }
}
